import React, { useState, useEffect } from 'react';

import Collapse from 'core/components/Collapse';
import { Icon, ICONS } from 'core/components/Icon';
import * as Typo from 'core/styles/Typography';
import { JssTextType } from 'core/utils/sitecore-utils';
import { executeCallback } from 'core/utils/global-utils';

import * as S from './InlineNotification.style';
import CONTENT from './InlineNotification.content';

export type NotificationType =
	| 'info'
	| 'success'
	| 'error'
	| 'warning'
	| 'loading';

export interface IProps {
	/** Visual styles/colours for the notification. */
	type?: NotificationType;
	/** Optionally include a button to dismiss/hide the notification. */
	hasCloseButton?: boolean;
	/** Optionally include a callback on close */
	handleClose?: () => void;
	/** CSS margin */
	margin?: string;
	/** Whether to parse RichText (for both JSS and non-JSS content). */
	isRichText?: boolean;
	/** Notification contents are either jss text object, or content string. */
	jss?: JssTextType;
	/** Notification contents are either jss text object, or content string. */
	title?: React.ReactNode | 'string';
	/** Notification contents are either jss text object, or content string. */
	content?: React.ReactNode | 'string';
	/** Generic className prop passthrough */
	className?: string;
	/** optional ICON if content author wants to override the generic icon of the Notification type */
	optionalIcon?: ICONS | string;
	/** optional track event handling */
	handleTrackEvent?: () => void;
	/** optional onClick event for content */
	contentOnClick?: React.MouseEventHandler<HTMLElement | React.ReactElement>;
	/** optional scroll to top on inline notification trigger */
	pagePostScrollToTop?: boolean;
	/** Close button label */
	closeButtonLabel?: string;
	/** Put close button on top, a hack for DbImportantNotice */
	topAlignCloseButton?: boolean;
	/** Animates icon */
	isAnimated?: boolean;
	/** Override for role for accessibility */
	role?: string;
}

const InlineNotification = ({
	type = 'info',
	hasCloseButton = false,
	handleClose,
	margin,
	title,
	content,
	jss,
	isRichText = false,
	className,
	optionalIcon,
	handleTrackEvent,
	contentOnClick,
	pagePostScrollToTop = false,
	closeButtonLabel,
	topAlignCloseButton = false,
	isAnimated = false,
	role,
}: IProps) => {
	const [isDismissed, setIsDismissed] = useState(false);

	useEffect(() => {
		/**
		 * Note: This will trigger on every mount but is countered by
		 * the nature of InlineNotification's structure which is inside a conditional statement
		 * from the consuming component.
		 */
		if (pagePostScrollToTop) {
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}
	}, []);

	return (
		<Collapse isOpen={!isDismissed} className={className}>
			<S.Wrapper
				{...{
					margin,
					type,
					hasCloseButton,
					optionalIcon,
					isAnimated,
					...{ hasCloseButtonLabel: !!closeButtonLabel },
					role: role ?? 'alertdialog',
				}}
			>
				{title && (
					<Typo.BodySmall
						data-target-id={`inline-notification--${type}`}
						margin="0"
						isBold
						{...{ isRichText }}
						onClick={contentOnClick}
					>
						{title}
					</Typo.BodySmall>
				)}
				<Typo.BodySmall
					data-target-id={`inline-notification--${type}`}
					margin="0"
					jss={jss}
					{...{ isRichText }}
					onClick={contentOnClick}
				>
					{!jss && content}
				</Typo.BodySmall>
				{hasCloseButton && (
					<S.CloseButton
						hasCloseButtonLabel={!!closeButtonLabel}
						type="button"
						aria-label={CONTENT.DISMISS}
						onClick={() => {
							handleTrackEvent?.();
							if (handleClose) {
								executeCallback(handleClose);
							}

							setIsDismissed(true);
						}}
						moveToTop={topAlignCloseButton}
					>
						{!!closeButtonLabel && (
							<Typo.BodySmall>{closeButtonLabel}</Typo.BodySmall>
						)}
						<Icon name={ICONS.CLOSE} size={24} />
					</S.CloseButton>
				)}
			</S.Wrapper>
		</Collapse>
	);
};

export default InlineNotification;
